/**
 * TechwriterMetadataEditor
 * ------------------------
 * WHAT: Two-field editor for tech-writer-only metadata on a content node:
 *       - `abstract`: short summary shown in listings (TipTap rich text).
 *       - `href`:     URL path slug used for deep links.
 *
 * WHY: Tech writers need to tweak these independently of the main content
 *      body. They're hidden from authors (visible=false) unless the current
 *      user has the tech-writer role.
 *
 * VISIBILITY RULES:
 *   - `visible=false` -> renders null (caller can mount this unconditionally).
 *   - Abstract is hidden for Section-type nodes and for Pattern Documents
 *     (patterns generate abstract from summary section automatically).
 *   - href is always shown when visible=true.
 */
import { ColumnLayout, Container, FormField } from '@amzn/awsui-components-core';
import { ContentNodeType, ContentType } from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { ContentSectionKey } from '../../../model/ContentSchemaSection';
import TechwriterMetadata from '../../../model/TechwriterMetadata';
import { hrefDisplayNames } from '../../utils/DisplayNames';
import MetadataStringInput from './MetadataStringInput';
import EditorWrapper from '../../../editor/EditorWrapper';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { TiptapMenuType } from '../../../editor/utils';

interface Props {
  readonly readonly: boolean;
  readonly metadata?: TechwriterMetadata; // Can be undefined — see BUG below.
  readonly visible: boolean; // Master switch; when false, nothing renders.
  readonly onInputUpdated?: (
    sectionKey: ContentSectionKey,
    value: any,
    validationError?: string,
  ) => void;
  readonly nodeType?: ContentNodeType; // Document vs Section — affects abstract visibility.
  readonly disabled?: boolean; // DEAD PROP: destructured but never passed through to editors.
  readonly metadataId?: string; // Used as part of the EditorWrapper id for stable DOM targeting.
  readonly contentType?: ContentType;
}

const TechwriterMetadataEditor = (props: Props) => {
  const { readonly, metadata, nodeType, onInputUpdated, visible, disabled, metadataId } = props;
  // BUG: destructures from `metadata` before the `visible` guard below.
  // If a caller passes `metadata={undefined}` along with `visible=true` this
  // throws "Cannot destructure property 'abstract' of undefined". Move the
  // visibility guard above this line, or default metadata to {}.
  // @ts-expect-error TS2339 - strict migration
  const { abstract, href } = metadata;

  // Wrapper that adapts the editor's TiptapOutputType to the shared callback.
  const updateAbstract = (content: TiptapOutputType) => {
    if (onInputUpdated) {
      onInputUpdated(ContentSectionKey.Abstract, content);
    }
  };

  // Short-circuit render. All hooks-free so safe to early-return.
  if (!visible) {
    return null;
  }

  // Abstract hidden for:
  //   - Section nodes (sections don't have their own abstract).
  //   - Pattern documents (abstract is auto-derived from the "summary" section).
  const hideAbstract =
    nodeType === ContentNodeType.Section ||
    (nodeType === ContentNodeType.Document && props.contentType === ContentType.Pattern);

  // Hide abstract for Section Node and Document of patterns
  const abstractEditor = hideAbstract ? null : (
    <div className="editorbox-small" key="abstractEditorDiv">
      <FormField label={'Abstract'} stretch>
        <EditorWrapper
          model={abstract}
          readOnly={readonly}
          onModelChange={updateAbstract}
          // Stable id per node so Tiptap can keep its internal editor state
          // when parents re-render. Prefixed with 'abstract-' to avoid clashes
          // with other editors on the page.
          id={'abstract-' + metadataId}
          menuType={TiptapMenuType.Hide}
        />
        {/* BUG: `disabled` prop is NEVER applied to EditorWrapper. If the
            caller passes disabled=true, the editor is still editable.
            Readonly works, but "disabled" is silently ignored. */}
      </FormField>
    </div>
  );

  // Label varies by node type — e.g. "URL" vs "Path slug" vs "Subpath".
  // Falls back to Document-style when nodeType is missing.
  const hrefLabel = hrefDisplayNames.get(nodeType || ContentNodeType.Document);
  const hrefEditor = (
    <div className="editorbox-small" key="hrefEditorDiv">
      <MetadataStringInput
        sectionId={ContentSectionKey.Href}
        readonly={readonly}
        stretch
        value={href}
        // @ts-expect-error TS2322 - strict migration
        label={hrefLabel}
        placeholder={`Provide ${hrefLabel}. For Tech writers only.`}
        // @ts-expect-error TS2322 - strict migration
        onInputUpdated={onInputUpdated}
        visible={visible}
      />
    </div>
  );

  // Collect non-null editors; one column when there's only one, two when both.
  const editors = [abstractEditor, hrefEditor].filter((e) => e);

  return (
    <Container className="techwriter-metadata-container">
      {/* Readonly view: display side-by-side (more compact).
          Edit view: stack vertically (so each field gets full width for editing). */}
      <ColumnLayout columns={readonly ? editors.length : 1} variant="text-grid">
        {editors}
      </ColumnLayout>
    </Container>
  );
};

export default TechwriterMetadataEditor;
